/* File: frontend/src/components/dashboard/ClientCaseStatusWidget.js */
/* Version: 5.0 (Platinum Polish - Visual Consistency) */
/* Date: 2025-12-10 13:20:00 EST */
/* Description: 
 * - UI: Aligned with RecentActivityWidget (Vertical lines, Dots).
 * - UX: Added 'Pulse' animation for the Current Stage.
 * - LOGIC: Differentiates Completed (Green), Current (Blue), and Future (Gray) steps.
 */

import React from 'react';
import PropTypes from 'prop-types';
import { Briefcase, Clock, CheckCircle2, Circle } from 'lucide-react';

// ── ПАЛИТРА ЭКРАНА «СЕГОДНЯ» → §3.1 (Ф1, quick-260909-jl8) ─────────────────
// Откат в каждой строке — ПРЕЖНИЙ литерал ЭТОГО ЖЕ места. На fitenko токен
// объявлен (styles/fitenko-tokens.css, селектор html.fitenko) и берётся он;
// на MLG и ELN токена НЕТ — берётся откат, то есть РОВНО прежний цвет.
// 🔴 Значения отката менять нельзя: ими доказывается неизменность чужих арендаторов.
// 🔴 Голый var(--ink) запрещён: у MLG он разрешается в ПУСТОТУ (отсутствие цвета),
//    а не в другой цвет — объявление становится недействительным.
// Класс C (подложки чипов) — по решению владельца от 2026-09-09, вариант B:
// подложка выводится правилом color-mix(in srgb, АКЦЕНТ 14%, --surface); правило
// объявлено ОДИН раз в pages/Dashboard.css под селектором html.fitenko. Бирюза —
// вариант T1: своего тона в §3.1 нет, берётся --green-accent.
const P = {
    canvas:      'var(--canvas, #f8fafc)',  // класс A
    ink:         'var(--ink, #1e293b)',  // класс A
    line:        'var(--line, #e2e8f0)',  // класс A
    lineStrong:  'var(--line, #cbd5e1)',  // класс A
    muted:       'var(--ink-muted, #64748b)',  // класс A
    mutedSoft:   'var(--ink-muted, #94a3b8)',  // класс A
    surfaceSoft: 'var(--surface-soft, #f1f5f9)',  // класс A
    blue1:       'var(--blue-accent, #3b82f6)',  // класс B
    blue3:       'var(--blue-accent, #1d4ed8)',  // класс B
    green1:      'var(--green-accent, #22c55e)',  // класс B
    chipBlue1:   'var(--chip-blue-bg, #eff6ff)',  // класс C
    chipBlue2:   'var(--chip-blue-bg, #dbeafe)',  // класс C
};




const ClientCaseStatusWidget = ({ data }) => {
    if (!data) return null;
    const { case_name, status, timeline } = data;

    // Platinum Card Style
    const cardStyle = {
        background: 'white',
        borderRadius: '16px',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)',
        border: `1px solid ${P.surfaceSoft}`,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
    };

    return (
        <div className="card" style={cardStyle}>
            {/* Animation for Current Stage */}
            <style>{`
                @keyframes pulse-blue {
                    0% { box-shadow: 0 0 0 0 rgba(59, 130, 246, 0.4); }
                    70% { box-shadow: 0 0 0 6px rgba(59, 130, 246, 0); }
                    100% { box-shadow: 0 0 0 0 rgba(59, 130, 246, 0); }
                }
                .custom-scrollbar::-webkit-scrollbar { width: 4px; }
                .custom-scrollbar::-webkit-scrollbar-track { background: ${P.surfaceSoft}; }
                .custom-scrollbar::-webkit-scrollbar-thumb { background: ${P.lineStrong}; borderRadius: 4px; }
            `}</style>

            {/* HEADER */}
            <div className="card-header" style={{
                padding: '20px 24px',
                borderBottom: `1px solid ${P.canvas}`,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start'
            }}>
                <div>
                    <h3 className="card-title" style={{
                        margin: 0, fontSize: '1.1rem', fontWeight: '700', color: P.ink,
                        display: 'flex', alignItems: 'center', gap: '10px'
                    }}>
                        <Briefcase size={20} style={{ stroke: P.blue1 }}/> {case_name || 'Current Case'}
                    </h3>
                    <div style={{ fontSize: '0.75rem', color: P.muted, marginTop: '4px', marginLeft: '30px' }}>
                        Track your case progress
                    </div>
                </div>
                <span style={{
                    background: P.chipBlue1, color: P.blue3, border: `1px solid ${P.chipBlue2}`,
                    padding: '4px 10px', borderRadius: '20px', fontSize: '0.75rem', fontWeight: '700',
                    textTransform: 'uppercase', letterSpacing: '0.5px'
                }}>
                    {status || 'Active'}
                </span>
            </div>
            
            {/* TIMELINE FEED */}
            <div className="custom-scrollbar" style={{ padding: '24px', overflowY: 'auto', flex: 1 }}>
                {timeline && timeline.map((step, idx) => {
                    // Logic to determine state
                    const isCompleted = step.completed;
                    const isCurrent = !isCompleted && (idx === 0 || timeline[idx - 1].completed);
                    const isFuture = !isCompleted && !isCurrent;
                    const isLast = idx === timeline.length - 1;

                    // Colors
                    let dotColor = P.line; // Gray (Future)
                    let lineColor = P.line;
                    let textColor = P.mutedSoft;
                    let icon = <Circle size={10} color="white" fill={dotColor} />;

                    if (isCompleted) {
                        dotColor = P.green1; // Green
                        lineColor = P.green1;
                        textColor = P.ink;
                        icon = <CheckCircle2 size={12} color="white" />;
                    } else if (isCurrent) {
                        dotColor = P.blue1; // Blue
                        textColor = P.ink;
                        icon = <Circle size={10} color="white" style={{ fill: P.blue1 }} />;
                    }

                    return (
                        <div key={idx} style={{ position: 'relative', paddingLeft: '24px', paddingBottom: isLast ? '0' : '28px' }}>
                            {/* Vertical Line */}
                            {!isLast && (
                                <div style={{
                                    position: 'absolute', left: '11px', top: '24px', bottom: '-4px',
                                    width: '2px', backgroundColor: isCompleted ? lineColor : P.surfaceSoft,
                                    zIndex: 0
                                }}></div>
                            )}

                            {/* Dot / Icon */}
                            <div style={{
                                position: 'absolute', left: '0', top: '4px',
                                width: '24px', height: '24px', borderRadius: '50%',
                                backgroundColor: dotColor, 
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                zIndex: 2,
                                border: '2px solid white',
                                boxShadow: isCurrent ? '0 0 0 4px rgba(59, 130, 246, 0.2)' : 'none',
                                animation: isCurrent ? 'pulse-blue 2s infinite' : 'none'
                            }}>
                                {icon}
                            </div>
                            
                            {/* Content */}
                            <div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <span style={{ 
                                        fontWeight: isCurrent ? '700' : '600', 
                                        fontSize: '0.9rem', 
                                        color: textColor 
                                    }}>
                                        {step.step}
                                    </span>
                                    {step.date && (
                                        <span style={{ 
                                            fontSize: '0.75rem', color: isCurrent ? P.blue1 : P.mutedSoft, 
                                            display: 'flex', alignItems: 'center', gap: '4px',
                                            background: isCurrent ? P.chipBlue1 : 'transparent',
                                            padding: isCurrent ? '2px 6px' : '0', borderRadius: '4px'
                                        }}>
                                            <Clock size={12}/> {step.date}
                                        </span>
                                    )}
                                </div>
                                {isCurrent && (
                                    <div style={{ fontSize: '0.8rem', color: P.muted, marginTop: '4px' }}>
                                        Processing currently...
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

ClientCaseStatusWidget.propTypes = { data: PropTypes.object };
export default ClientCaseStatusWidget;